// web-frontend/src/shared/services/localNotificationService.js
// 🔥 웹 환경 호환 버전 (Expo 의존성 제거)

import logger from '../utils/logger';

/**
 * 웹 환경에서는 브라우저의 Notification API를 사용
 * 모바일에서는 expo-notifications 사용
 */

class LocalNotificationService {
  constructor() {
    this.isSupported = 'Notification' in window;
    this.permission = this.isSupported ? Notification.permission : 'denied';
    
    logger.debug('📱 LocalNotificationService (웹버전) 초기화:', {
      isSupported: this.isSupported,
      permission: this.permission
    });
  }

  /**
   * 알림 권한 요청
   */
  async requestPermissions() {
    if (!this.isSupported) {
      logger.warn('⚠️ 브라우저가 알림을 지원하지 않습니다.');
      return { granted: false };
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      logger.debug('🔔 알림 권한:', permission);
      
      return {
        granted: permission === 'granted',
        status: permission
      };
    } catch (error) {
      logger.error('❌ 알림 권한 요청 실패:', error);
      return { granted: false, error };
    }
  }

  /**
   * 즉시 알림 표시
   */
  async showNotification(title, options = {}) {
    if (!this.isSupported) {
      logger.debug('ℹ️ 알림 미지원:', title);
      return null;
    }

    if (this.permission !== 'granted') {
      logger.warn('⚠️ 알림 권한이 없습니다.');
      const result = await this.requestPermissions();
      if (!result.granted) {
        return null;
      }
    }

    try {
      const notification = new Notification(title, {
        body: options.body || options.message || '',
        icon: options.icon || '/favicon.ico',
        badge: options.badge || '/favicon.ico',
        tag: options.tag || `notification-${Date.now()}`,
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || false,
        data: options.data || {},
        ...options
      });

      // 클릭 이벤트 핸들러
      if (options.onClick) {
        notification.onclick = () => {
          options.onClick();
          notification.close();
        };
      }

      // 자동 닫기
      if (options.autoClose !== false) {
        setTimeout(() => {
          notification.close();
        }, options.duration || 5000);
      }

      logger.debug('✅ 알림 표시:', title);
      return notification;

    } catch (error) {
      logger.error('❌ 알림 표시 실패:', error);
      return null;
    }
  }

  /**
   * 미팅 알림 스케줄링
   * 웹에서는 실제 스케줄링이 불가능하므로 로그만 남김
   */
  async scheduleMeetingNotifications(meeting) {
    logger.debug('📅 미팅 알림 스케줄 (웹 환경 - 로그만 기록):', {
      id: meeting.id,
      title: meeting.title,
      scheduled_start: meeting.scheduled_start || meeting.scheduled_at
    });

    // 웹에서는 실제 스케줄링이 어려우므로
    // 서비스 워커나 백엔드 알림 시스템과 연동 필요
    // 현재는 스텁으로 처리

    return {
      success: true,
      message: '웹 환경에서는 백엔드 알림 시스템을 사용합니다.',
      meetingId: meeting.id
    };
  }

  /**
   * 미팅 알림 취소
   */
  async cancelMeetingNotifications(meetingId) {
    logger.debug('🔕 미팅 알림 취소 (웹 환경):', meetingId);
    
    return {
      success: true,
      message: '웹 환경에서는 백엔드 알림 시스템을 사용합니다.',
      meetingId
    };
  }

  /**
   * 미팅 알림 재스케줄
   */
  async rescheduleMeetingNotifications(meeting) {
    logger.debug('🔄 미팅 알림 재스케줄 (웹 환경):', {
      id: meeting.id,
      title: meeting.title,
      scheduled_start: meeting.scheduled_start || meeting.scheduled_at
    });

    return {
      success: true,
      message: '웹 환경에서는 백엔드 알림 시스템을 사용합니다.',
      meetingId: meeting.id
    };
  }

  /**
   * 모든 예약된 알림 조회
   */
  async getAllScheduledNotifications() {
    logger.debug('📋 예약된 알림 조회 (웹 환경 - 빈 배열 반환)');
    
    // 웹에서는 예약된 알림을 관리하지 않음
    return [];
  }

  /**
   * 특정 알림 취소
   */
  async cancelScheduledNotification(notificationId) {
    logger.debug('🔕 알림 취소 (웹 환경):', notificationId);
    
    return {
      success: true,
      message: '웹 환경에서는 백엔드 알림 시스템을 사용합니다.'
    };
  }

  /**
   * 모든 알림 취소
   */
  async cancelAllScheduledNotifications() {
    logger.debug('🔕 모든 알림 취소 (웹 환경)');
    
    return {
      success: true,
      message: '웹 환경에서는 백엔드 알림 시스템을 사용합니다.'
    };
  }

  /**
   * 알림 채널 생성 (Android 전용, 웹에서는 무시)
   */
  async setNotificationChannelAsync(channelId, channelConfig) {
    logger.debug('📢 알림 채널 생성 (웹 환경 - 무시):', channelId);
    return { success: true };
  }

  /**
   * 배지 카운트 설정 (iOS 전용, 웹에서는 무시)
   */
  async setBadgeCountAsync(count) {
    logger.debug('🔢 배지 카운트 설정 (웹 환경 - 무시):', count);
    return { success: true };
  }

  /**
   * 푸시 토큰 가져오기 (웹에서는 서비스 워커 필요)
   */
  async getExpoPushTokenAsync() {
    logger.debug('🔑 푸시 토큰 조회 (웹 환경 - 미지원)');
    
    // 웹에서는 Firebase Cloud Messaging 등 별도 구현 필요
    return {
      type: 'web',
      data: null,
      message: '웹 환경에서는 FCM 등 별도 푸시 서비스 필요'
    };
  }

  /**
   * 알림 수신 리스너 등록
   */
  addNotificationReceivedListener(callback) {
    logger.debug('👂 알림 수신 리스너 등록 (웹 환경)');
    
    // 웹에서는 Service Worker를 통해 구현 가능
    // 현재는 스텁으로 처리
    
    return {
      remove: () => {
        logger.debug('🔇 알림 수신 리스너 제거');
      }
    };
  }

  /**
   * 알림 응답 리스너 등록
   */
  addNotificationResponseReceivedListener(callback) {
    logger.debug('👂 알림 응답 리스너 등록 (웹 환경)');
    
    return {
      remove: () => {
        logger.debug('🔇 알림 응답 리스너 제거');
      }
    };
  }

  /**
   * 테스트 알림
   */
  async testNotification() {
    return this.showNotification('DSK 테스트', {
      body: '알림이 정상적으로 작동합니다! 🎉',
      icon: '/favicon.ico',
      tag: 'test-notification',
      requireInteraction: false,
      autoClose: true,
      duration: 3000
    });
  }

  /**
   * 알림 권한 상태 확인
   */
  getPermissionStatus() {
    if (!this.isSupported) {
      return 'unsupported';
    }
    
    return this.permission;
  }

  /**
   * 알림 지원 여부 확인
   */
  isNotificationSupported() {
    return this.isSupported;
  }
}

// 싱글톤 인스턴스 생성
const localNotificationService = new LocalNotificationService();

export default localNotificationService;
export { localNotificationService };